import React, { useRef } from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import { LoggedOutProductLibraryContainsItem } from '../../types/library';
import LibraryL5PageLoggedOut from './LibraryL5PageLoggedOut';
import ScrollToElement from '@osmosis/nextjs-components/ScrollToElement';

type LibraryL4LevelProps = {
  l1Id: string;
  l2Id: string;
  l3Id: string;
  l4: LoggedOutProductLibraryContainsItem;
  targetId?: string;
};

/**
 * Component representing a logged-out view of a Level 4 library page.
 * @param {Object} props - The properties object.
 * @param {string} props.l1Id - The ID of the Level 1 library item.
 * @param {string} props.l2Id - The ID of the Level 2 library item.
 * @param {string} props.l3Id - The ID of the Level 3 library item.
 * @param {LoggedOutProductLibraryContainsItem} props.l4 - The Level 4 library item.
 * @param {string} [props.targetId] - The ID of the target element to scroll to.
 * @returns {JSX.Element} The rendered component.
 */
const LibraryL4PageLoggedOut = ({
  l1Id,
  l2Id,
  l3Id,
  l4,
  targetId,
}: LibraryL4LevelProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const l4Id = encodeURI(`${l1Id}-${l2Id}-${l3Id}-${l4.id}-L4`);
  const isTargetElement = targetId === l4Id;

  return (
    <Box
      className="w-full md:w-[30%] rounded-lg flex flex-col gap-2"
      key={l4.name}
      data-id={l4.name}
      ref={elementRef}
    >
      {isTargetElement && <ScrollToElement elementId={l4Id} />}
      <H4 data-testid="library-topic-box-l4-level-name" id={l4Id}>
        {l4.displayName || l4.name}
      </H4>

      {l4?.contains?.map((l5: LoggedOutProductLibraryContainsItem, index) => (
        <LibraryL5PageLoggedOut
          key={'l5' + index + l5.id}
          l1Id={l1Id}
          l2Id={l2Id}
          l3Id={l3Id}
          l4Id={l4.id}
          l5={l5}
          {...(targetId && { targetId })}
        />
      ))}
    </Box>
  );
};

export default LibraryL4PageLoggedOut;
